import {
  DeleteOutlined,
  DownloadOutlined,
  EyeInvisibleOutlined,
  GlobalOutlined,
  SaveOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { round } from '@mahawi/eshop-common/src/round';
import { stringProbableMatch } from '@mahawi/eshop-common/src/string-probable-match';
import { getTranslation } from '@mahawi/eshop-common/src/translation';
import {
  EAdminWarehouseProductType,
  EFeatureFlag,
  type IAdminProduct,
  type IAdminWarehouseProductDetail,
  type IProperty,
  type IWarehouseCategory,
  type IWarehouseDetail,
  type IWarehouseProductFeatureFlag,
} from '@mahawi/eshop-common/src/types';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Divider,
  Popconfirm,
  Row,
  Skeleton,
  Space,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import Markdown from 'markdown-to-jsx';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import CopyToClipboard from '../components/copy-to-clipboard';
import DetailPageHeader from '../components/detail-page-header';
import WarehouseProductAdd from '../components/downloader/warehouse-product-add';
import WarehouseProductSuggestions from '../components/downloader/warehouse-product-suggestions';
import ExternalLink from '../components/external-link';
import Link from '../components/link';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { featureFlagTags } from '../fragments/feature-flag-tags';
import { normalizeWarehouseProductName } from '../fragments/normalize-warehouse-product-name';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useSocketSubscription } from '../hooks/use-socket-subscription';
import { type RootState } from '../reducers';
import {
  downloaderDeleteWarehouseProduct,
  downloaderDownloadAllPhotosWarehouseProduct,
  downloaderHideWarehouseProduct,
  downloaderLoadWarehouseProduct,
  downloaderSyncWarehouseProduct,
  downloaderWarehouseProductPairWithExistingProduct,
} from '../reducers/downloader/reducer';
import {
  productsGetByGTIN,
  productsGetByMPN,
} from '../reducers/products/reducer';
import { loadPropertiesByRawNames } from '../reducers/properties/reducer';
import Socket from '../socket';
import { SPACING } from '../theme/tokens';

const categoriesHeaderEl: React.ReactElement = (
  <Row gutter={SPACING.lg} key="header">
    <Col span={10}>
      <Typography.Paragraph strong>
        Warehouse category name
      </Typography.Paragraph>
    </Col>

    <Col span={2}>
      <Typography.Paragraph strong>Actions</Typography.Paragraph>
    </Col>

    <Col span={10}>
      <Typography.Paragraph strong>Category name</Typography.Paragraph>
    </Col>

    <Col span={2}>
      <Typography.Paragraph strong>Actions</Typography.Paragraph>
    </Col>
  </Row>
);

const propertiesHeaderEl: React.ReactElement = (
  <Row gutter={SPACING.lg} key="header">
    <Col span={10}>
      <Typography.Paragraph strong>Property name</Typography.Paragraph>
    </Col>

    <Col span={14}>
      <Typography.Paragraph strong>Value</Typography.Paragraph>
    </Col>
  </Row>
);

function DownloaderWarehouseProduct({
  socket = undefined,
}: {
  socket?: Socket;
}): React.ReactElement {
  const dispatch = useAppDispatch();
  const warehouseProduct = useSelector(
    (s: RootState) => s.Downloader.warehouse.product,
  );
  const downloaderInProcess = useSelector(
    (s: RootState) => s.Downloader.inProcess,
  );
  const newProduct = useSelector((s: RootState) => s.Downloader.newProduct);
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const productsProducts = useSelector((s: RootState) => s.Products.products);
  const featureFlagsState = useSelector((s: RootState) => s.FeatureFlags);
  const warehouseState = useSelector((s: RootState) => s.Warehouse);
  const propertiesProperties = useSelector(
    (s: RootState) => s.Properties.properties,
  );
  const propertiesUpdatedAt = useSelector(
    (s: RootState) => s.Properties.updatedAt,
  );

  const { uuid } = useParams();
  const [showPairedProperties, setShowPairedProperties] =
    useState<boolean>(false);
  const [showPairedCategories, setShowPairedCategories] =
    useState<boolean>(false);
  const [selectedCategoryUUIDs, setSelectedCategoryUUIDs] = useState<string[]>(
    [],
  );

  useEffect((): void => {
    setSelectedCategoryUUIDs([]);
  }, [uuid]);

  const reload = useCallback((): void => {
    if (uuid) {
      dispatch(downloaderLoadWarehouseProduct(uuid));
    }
  }, [uuid, dispatch]);

  useEffect((): void => {
    reload();
  }, [reload]);

  const nameNormalized = useMemo(
    (): string =>
      warehouseProduct?.name
        ? normalizeWarehouseProductName(warehouseProduct.name)
        : '',
    [warehouseProduct?.name],
  );

  const product = useMemo(
    (): IAdminWarehouseProductDetail | null =>
      warehouseProduct?.type === EAdminWarehouseProductType.DETAIL
        ? warehouseProduct
        : null,
    [warehouseProduct],
  );

  const categoriesEl = useMemo((): React.ReactElement[] => {
    const categoriesUE: React.ReactElement[] =
      product?.categories
        .map((category: IWarehouseCategory): React.ReactElement | null => {
          const categoryName: string | null = getTranslation(
            category.category?.names,
            languageType,
          );

          if (!showPairedCategories && category.category) {
            return null;
          }

          let pairedCategory: React.ReactElement = (
            <>
              <Col span={10}></Col>
              <Col span={2}></Col>
            </>
          );

          if (category.category) {
            pairedCategory = (
              <>
                <Col span={10}>
                  {category.category && (
                    <Typography.Paragraph>{categoryName}</Typography.Paragraph>
                  )}
                </Col>

                <Col span={2}>
                  <Space size={SPACING.sm} align="baseline">
                    {category.category?.uuid && (
                      <Link
                        to={`/category/${category.category.uuid}`}
                        openInNewTab
                      />
                    )}
                  </Space>
                </Col>
              </>
            );
          }

          return (
            <Row key={category.uuid} gutter={SPACING.lg}>
              <Col span={10}>
                <Typography.Paragraph>{category.name}</Typography.Paragraph>
              </Col>

              <Col span={2}>
                <Space size={SPACING.sm} align="baseline">
                  <CopyToClipboard text={category.name} type="link" />

                  <Link
                    to={`/downloader/warehouse-category/${category.uuid}`}
                    openInNewTab
                  />
                </Space>
              </Col>

              {pairedCategory}
            </Row>
          );
        })
        .filter(notEmpty) || [];

    return [...[categoriesHeaderEl], ...categoriesUE];
  }, [product?.categories, languageType?.code, showPairedCategories]);

  const productsSimilar = useMemo((): React.ReactElement[] => {
    if (!productsProducts) {
      return [];
    }

    return productsProducts.map(
      ({
        names,
        uuid: productUUID,
        gtin,
      }: IAdminProduct): React.ReactElement => {
        const productName: string | null = getTranslation(names, languageType);

        return (
          <Row key={productUUID} gutter={SPACING.lg}>
            <Col span={10}>
              <Typography.Paragraph>{productName}</Typography.Paragraph>
            </Col>

            <Col span={2}>
              <Typography.Paragraph>{gtin || EMPTY_VALUE}</Typography.Paragraph>
            </Col>

            <Col span={2}>
              <Space size={SPACING.sm} style={{ width: '100%' }}>
                <Link to={`/product/${productUUID}`} openInNewTab />
                <Popconfirm
                  placement="bottom"
                  title="Are you sure to pair with this product?"
                  description="Warehouse product will be paired with this product."
                  onConfirm={(): void => {
                    if (warehouseProduct?.uuid) {
                      dispatch(
                        downloaderWarehouseProductPairWithExistingProduct(
                          productUUID,
                          warehouseProduct.uuid,
                        ),
                      );
                      dispatch(
                        downloaderSyncWarehouseProduct(
                          productUUID,
                          warehouseProduct.uuid,
                        ),
                      );
                    }
                  }}
                >
                  <SaveOutlined />
                </Popconfirm>
              </Space>
            </Col>

            <Col span={2}>
              <Typography.Paragraph>
                {round(
                  stringProbableMatch({
                    first: productName,
                    second: nameNormalized,
                  }) * 100,
                  0,
                )}{' '}
                %
              </Typography.Paragraph>
            </Col>
          </Row>
        );
      },
    );
  }, [
    productsProducts,
    languageType?.code,
    warehouseProduct?.uuid,
    nameNormalized,
    dispatch,
  ]);

  const featureFlagEls = useMemo(
    (): React.ReactElement[] =>
      featureFlagTags({
        productFeatureFlags: warehouseProduct?.featureFlags ?? [],
        FeatureFlags: featureFlagsState,
        Warehouse: warehouseState,
      }),
    [
      warehouseProduct?.featureFlags,
      featureFlagsState.featureFlags,
      featureFlagsState.updatedAt,
      warehouseState.warehouses,
    ],
  );

  const warehouse = useMemo(
    (): IWarehouseDetail | undefined =>
      warehouseState.warehouses?.find(
        (w: IWarehouseDetail): boolean =>
          w.uuid === warehouseProduct?.warehouse?.uuid,
      ),
    [warehouseProduct?.warehouse?.uuid, warehouseState.warehouses],
  );

  useEffect((): void => {
    const rawNames: string[] = Object.keys(warehouseProduct?.properties || {});

    if (rawNames.length > 0 && propertiesProperties === undefined) {
      dispatch(loadPropertiesByRawNames(rawNames));
    }
  }, [warehouseProduct?.properties, propertiesProperties, dispatch]);

  const propertiesEls = useMemo((): React.ReactElement[] => {
    const rawNames: string[] = Object.keys(warehouseProduct?.properties || {});

    const els: React.ReactElement[] = (
      rawNames.map((rawName: string): React.ReactElement | null => {
        const rawNameNormalized: string = rawName.toLocaleLowerCase().trim();
        const value: string | null | undefined =
          warehouseProduct &&
          warehouseProduct.properties &&
          warehouseProduct.properties[rawName];

        const property: IProperty | undefined = propertiesProperties?.find(
          (prop: IProperty): boolean =>
            prop.warehouseProperty?.assignedRawNames?.find(
              (assignedRawName: string): boolean =>
                assignedRawName === rawNameNormalized,
            ) !== undefined,
        );

        if (!showPairedProperties && property) {
          return null;
        }

        return (
          <Row key={rawName} gutter={SPACING.lg}>
            <Col span={10}>
              <Typography.Paragraph>
                <Space size={SPACING.xs} align="center">
                  {rawName}

                  {property && (
                    <Link to={`/property/${property?.code}`} openInNewTab />
                  )}
                  {!property && (
                    <CopyToClipboard text={rawName} type="link" size="small" />
                  )}
                </Space>
              </Typography.Paragraph>
            </Col>

            <Col span={14}>
              <Typography.Paragraph>{value}</Typography.Paragraph>
            </Col>
          </Row>
        );
      }) || []
    ).filter(notEmpty);

    return [...[propertiesHeaderEl], ...els];
  }, [
    warehouseProduct?.properties,
    propertiesProperties,
    propertiesUpdatedAt,
    showPairedProperties,
  ]);

  useSocketSubscription(
    socket,
    (s) => s.joinWarehouseProductRoom({ warehouseProduct }),
    [warehouseProduct?.uuid],
  );

  if (warehouseProduct === undefined || !uuid) {
    return <Skeleton active />;
  }

  if (warehouseProduct === null) {
    return (
      <Alert
        title="Warehouse product not found"
        description={`Warehouse product with uuid ${uuid} not found.`}
        type="error"
        showIcon
      />
    );
  }

  return (
    <Space size={SPACING.sm} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Warehouse product"
        onReload={reload}
        isReloadDisabled={downloaderInProcess}
        actions={
          <>
            <Tooltip title="Sync product with warehouse">
              <Button
                icon={<SyncOutlined />}
                onClick={(): void => {
                  if (uuid) {
                    dispatch(downloaderSyncWarehouseProduct(undefined, uuid));
                  }
                }}
                disabled={downloaderInProcess}
              >
                Sync
              </Button>
            </Tooltip>

            <Button
              icon={<DownloadOutlined />}
              onClick={(): void => {
                if (uuid) {
                  dispatch(downloaderDownloadAllPhotosWarehouseProduct(uuid));
                }
              }}
              disabled={downloaderInProcess}
            >
              Download all photos
            </Button>

            <Popconfirm
              placement="bottom"
              title="Are you sure to hide this warehouse product?"
              description="Warehouse product will be hidden in warehouse product lists."
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                if (warehouseProduct?.uuid) {
                  dispatch(
                    downloaderHideWarehouseProduct(warehouseProduct.uuid, true),
                  );
                }
              }}
            >
              <Button
                icon={<EyeInvisibleOutlined />}
                disabled={
                  downloaderInProcess ||
                  warehouseProduct.featureFlags?.filter(
                    (flag: IWarehouseProductFeatureFlag): boolean =>
                      flag.code === EFeatureFlag.WAREHOUSE_PRODUCT_HIDDEN,
                  ).length > 0
                }
              >
                Hide
              </Button>
            </Popconfirm>

            <Popconfirm
              placement="bottom"
              title="Are you sure to delete this product?"
              description="Warehouse product will be deleted, action cannot be undone."
              okText="Yes"
              cancelText="No"
              onConfirm={(): void => {
                if (warehouseProduct?.uuid) {
                  dispatch(
                    downloaderDeleteWarehouseProduct(warehouseProduct.uuid),
                  );
                }
              }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={downloaderInProcess}
              >
                Delete
              </Button>
            </Popconfirm>
          </>
        }
        tags={featureFlagEls}
        uuid={uuid}
      />

      <Descriptions
        bordered
        column={2}
        size="small"
        styles={{ content: { width: '50%' } }}
        items={[
          {
            key: 'uuid',
            label: 'UUID',
            children: (
              <Space size={SPACING.sm} align="baseline">
                {warehouseProduct.uuid}

                <CopyToClipboard text={warehouseProduct.uuid} type="link" />
              </Space>
            ),
          },
          {
            key: 'warehouse-name',
            label: 'Warehouse name',
            children: (
              <Space size={SPACING.sm} align="baseline">
                {warehouseProduct.warehouse.name}

                <CopyToClipboard
                  text={warehouseProduct.warehouse.name}
                  type="link"
                />
              </Space>
            ),
          },
          {
            key: 'name',
            label: 'Name',
            children: (
              <Space size={SPACING.sm} align="baseline">
                {warehouseProduct.name}

                <CopyToClipboard text={warehouseProduct.name} type="link" />
              </Space>
            ),
          },
          {
            key: 'warehouse-product-code',
            label: 'Warehouse product code',
            children: (
              <Space size={SPACING.sm} align="baseline">
                {warehouseProduct.productCode}

                <CopyToClipboard
                  text={warehouseProduct.productCode}
                  type="link"
                />

                {warehouseProduct.url ? (
                  <ExternalLink
                    to={warehouseProduct.url}
                    ariaLabel="Open external source URL in new tab"
                  />
                ) : (
                  <GlobalOutlined disabled />
                )}
              </Space>
            ),
          },
          {
            key: 'name-normalized',
            label: 'Name (normalized)',
            children: (
              <Space size={SPACING.sm} align="baseline">
                {nameNormalized}

                <CopyToClipboard text={nameNormalized} type="link" />
              </Space>
            ),
          },
          {
            key: 'brand',
            label: 'Brand',
            children: warehouseProduct.brand,
          },
          {
            key: 'prices',
            label: 'Prices',
            children: (
              <Space size={SPACING.sm} align="start" orientation="vertical">
                <Typography.Paragraph>
                  Purchase:{' '}
                  <Typography.Text strong>
                    {formatCurrency(
                      warehouseProduct.price,
                      warehouse?.currencyType,
                    )}
                  </Typography.Text>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Recommended:{' '}
                  <Typography.Text strong>
                    {formatCurrency(
                      warehouseProduct.recommendedPrice,
                      warehouse?.currencyType,
                    )}
                  </Typography.Text>
                </Typography.Paragraph>
                <Typography.Paragraph>
                  Margin:{' '}
                  <Typography.Text strong>
                    {formatCurrency(
                      warehouseProduct.recommendedPrice -
                        warehouseProduct.price,
                      warehouse?.currencyType,
                    )}
                  </Typography.Text>
                </Typography.Paragraph>
              </Space>
            ),
          },
          {
            key: 'identification',
            label: 'Identification',
            children: (
              <Space
                size={SPACING.sm}
                orientation="vertical"
                style={{ width: '100%' }}
              >
                <Space size={SPACING.sm} align="baseline">
                  <Typography.Paragraph>
                    MPN:{' '}
                    <Typography.Text strong>
                      {warehouseProduct.mpn}
                    </Typography.Text>
                  </Typography.Paragraph>

                  <CopyToClipboard text={warehouseProduct.mpn} type="link" />

                  <SearchOutlined
                    onClick={(): void => {
                      if (warehouseProduct?.mpn) {
                        dispatch(productsGetByMPN(warehouseProduct.mpn));
                      }
                    }}
                    disabled={!warehouseProduct?.mpn}
                  />
                </Space>

                <Space size={SPACING.sm} align="baseline">
                  <Typography.Paragraph>
                    GTIN:{' '}
                    <Typography.Text strong>
                      {warehouseProduct.gtin}
                    </Typography.Text>
                  </Typography.Paragraph>

                  {warehouseProduct.gtin && (
                    <CopyToClipboard text={warehouseProduct.gtin} type="link" />
                  )}

                  <SearchOutlined
                    onClick={(): void => {
                      if (warehouseProduct?.gtin) {
                        dispatch(productsGetByGTIN(warehouseProduct.gtin));
                      }
                    }}
                    disabled={!warehouseProduct?.gtin}
                  />
                </Space>

                {productsSimilar}
              </Space>
            ),
          },
          {
            key: 'weight',
            label: 'Weight',
            span: 1,
            children: (
              <Space size={SPACING.sm} align="baseline">
                {warehouseProduct.dimensions.weight} Kg
              </Space>
            ),
          },
          {
            key: 'properties',
            label: 'Properties',
            span: 1,
            children: (
              <>
                <Space size={SPACING.sm} align="baseline">
                  <Switch
                    onChange={(): void =>
                      setShowPairedProperties(!showPairedProperties)
                    }
                  />
                  <Typography.Text strong>Paired properties</Typography.Text>
                </Space>
                {propertiesEls}
              </>
            ),
          },
          {
            key: 'categories',
            label: 'Categories',
            span: 2,
            children: (
              <>
                <Space size={SPACING.sm} align="baseline">
                  <Switch
                    checked={showPairedCategories}
                    onChange={(checked: boolean): void =>
                      setShowPairedCategories(checked)
                    }
                  />
                  <Typography.Text strong>Paired categories</Typography.Text>
                </Space>

                {categoriesEl}
              </>
            ),
          },
          {
            key: 'item',
            label: (
              <Space
                size={SPACING.none}
                align="baseline"
                orientation="vertical"
              >
                <Typography.Text>Description</Typography.Text>
                <CopyToClipboard
                  text={product?.description || EMPTY_VALUE}
                  type="link"
                />
              </Space>
            ),
            span: 2,
            children: (
              <Markdown>{product?.description || EMPTY_VALUE}</Markdown>
            ),
          },
        ]}
      />

      <Divider />

      {!newProduct && !warehouseProduct.product.uuid && (
        <WarehouseProductSuggestions
          warehouseProductUuid={warehouseProduct.uuid}
          selectedCategoryUUIDs={selectedCategoryUUIDs}
          onSelectedCategoryUUIDsChange={setSelectedCategoryUUIDs}
        />
      )}

      {!newProduct && !warehouseProduct.product.uuid && nameNormalized && (
        <WarehouseProductAdd
          name={nameNormalized}
          overrideCategoryUUIDs={selectedCategoryUUIDs}
        />
      )}

      {newProduct && !warehouseProduct.product.uuid && (
        <Row>
          <Col span={14} offset={5}>
            <Link
              to={`/product/${newProduct.uuid}`}
              openInNewTab
              label={`Created new product with UUID ${newProduct.uuid}`}
              additionalSearchParams={{
                productCategories: 'open',
                productLanguage: 'open',
              }}
            />
          </Col>
        </Row>
      )}

      {!newProduct && warehouseProduct.product.uuid && (
        <Row>
          <Col span={14} offset={5}>
            <Link
              to={`/product/${warehouseProduct.product.uuid}`}
              openInNewTab
              label={`Existing product with UUID ${warehouseProduct.product.uuid}`}
              additionalSearchParams={{
                productCategories: 'open',
                productLanguage: 'open',
              }}
            />
          </Col>
        </Row>
      )}
    </Space>
  );
}

export default DownloaderWarehouseProduct;
